
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Local storage key for caching the ACH fee setting
export const ACH_FEE_STORAGE_KEY = 'keepmore_charge_ach_fees';
// Cache duration in milliseconds (5 minutes)
export const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get the current ACH fee setting from Supabase or cache.
 * This is an async function that will fetch from Supabase if needed.
 */
export const getChargeAchFees = async (): Promise<boolean> => {
  try {
    // First check if we have a recent cached value
    const cachedValue = getChargeAchFeesFromCache();
    if (cachedValue !== null) {
      console.log('[DEBUG][getChargeAchFees] Using cached value:', cachedValue);
      return cachedValue;
    }
    
    // If no valid cache, fetch from Supabase
    console.log('[DEBUG][getChargeAchFees] Fetching from Supabase...');
    
    // Get the current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      console.error('[DEBUG][getChargeAchFees] User error:', userError);
      // Default to false if we can't get the user
      return false;
    }
    
    // Query the workspace settings table for this user
    const { data, error } = await supabase
      .from('workspace_settings')
      .select('charge_ach_fees')
      .eq('user_id', user.id)
      .single();
    
    if (error) {
      console.error('[DEBUG][getChargeAchFees] Supabase error:', error);
      // Default to false if we can't get the setting
      return false;
    }
    
    // Get the setting value or default to false if not found
    const chargeAchFees = data?.charge_ach_fees === true;
    console.log('[DEBUG][getChargeAchFees] Fetched from Supabase:', chargeAchFees);
    
    // Save to cache
    setChargeAchFeesToCache(chargeAchFees);
    
    return chargeAchFees;
  } catch (err) {
    console.error('[DEBUG][getChargeAchFees] Unexpected error:', err);
    // Default to false on error
    return false;
  }
};

/**
 * Set the ACH fee setting in Supabase and update the cache.
 */
export const setChargeAchFees = async (chargeAchFees: boolean): Promise<boolean> => {
  try {
    console.log('[DEBUG][setChargeAchFees] Setting ACH fees to:', chargeAchFees);
    
    // Get the current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      console.error('[DEBUG][setChargeAchFees] User error:', userError);
      return false;
    }
    
    // Update the workspace_settings table
    const { error } = await supabase
      .from('workspace_settings')
      .upsert({ 
        user_id: user.id,
        charge_ach_fees: chargeAchFees 
      }, { 
        onConflict: 'user_id' 
      });
    
    if (error) {
      console.error('[DEBUG][setChargeAchFees] Supabase error:', error);
      return false;
    }
    
    // Update cache
    setChargeAchFeesToCache(chargeAchFees);
    
    return true;
  } catch (err) {
    console.error('[DEBUG][setChargeAchFees] Unexpected error:', err);
    return false;
  }
};

/**
 * Get the ACH fee setting from cache only (no API call).
 * Returns null if no valid cache exists.
 */
export const getChargeAchFeesFromCache = (): boolean | null => {
  try {
    const cachedData = localStorage.getItem(ACH_FEE_STORAGE_KEY);
    if (!cachedData) {
      return null;
    }
    
    const { value, timestamp } = JSON.parse(cachedData);
    const now = new Date().getTime();
    
    // Check if cache is still valid
    if (now - timestamp < CACHE_DURATION) {
      return value === true;
    } else {
      // Cache expired
      return null;
    }
  } catch (err) {
    console.error('[DEBUG][getChargeAchFeesFromCache] Cache error:', err);
    return null;
  }
};

/**
 * Save the ACH fee setting to cache with current timestamp.
 */
export const setChargeAchFeesToCache = (value: boolean): void => {
  try {
    const cacheData = {
      value,
      timestamp: new Date().getTime()
    };
    
    localStorage.setItem(ACH_FEE_STORAGE_KEY, JSON.stringify(cacheData));
    console.log('[DEBUG][setChargeAchFeesToCache] Updated cache:', value);
  } catch (err) {
    console.error('[DEBUG][setChargeAchFeesToCache] Cache error:', err);
  }
};

/**
 * Synchronous version that returns the cached value or a default.
 * Will never make an API call.
 */
export const getChargeAchFeesSync = (defaultValue: boolean = false): boolean => {
  const cachedValue = getChargeAchFeesFromCache();
  return cachedValue !== null ? cachedValue : defaultValue;
};

/**
 * Log detailed fee calculation information to the console
 * This is a synchronous version used for immediate logging
 */
export const logFeeCalculationSync = (
  netAmount: number,
  paymentMethod: 'bank' | 'card' = 'card'
) => {
  console.log('💰 Fee calculation (sync):');
  console.log(`  - Net amount to business: $${netAmount.toFixed(2)}`);
  console.log(`  - Payment method: ${paymentMethod}`);
  
  // Since this is a sync function, we don't use calculateStripeFee which might be async
  // Instead we calculate simple estimates
  const stripeFeePercent = paymentMethod === 'bank' ? 0.008 : 0.029;
  const stripeFeeFixed = paymentMethod === 'bank' ? 0 : 0.30;
  const platformFeePercent = paymentMethod === 'bank' ? 0 : 0.01;
  
  // Calculate the fees
  const stripeFee = (netAmount * stripeFeePercent / (1 - stripeFeePercent - platformFeePercent)) + 
                    (stripeFeeFixed / (1 - stripeFeePercent - platformFeePercent));
  const platformFee = netAmount * platformFeePercent / (1 - stripeFeePercent - platformFeePercent);
  const grossAmount = netAmount + stripeFee + platformFee;
  
  console.log(`  - Estimated Stripe fee: $${stripeFee.toFixed(2)}`);
  console.log(`  - Estimated platform fee: $${platformFee.toFixed(2)}`);
  console.log(`  - Estimated gross amount: $${grossAmount.toFixed(2)}`);
  
  return { stripeFee, platformFee, grossAmount };
};
